import { all, call, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { stringify } from 'utilities/apiQueryString'

import * as actions from './actions'
import { expireSession } from '../auth/actions'
import { success, failure } from 'utilities/actions'
import { getEndpointByDocType } from 'utilities/state'
import {
  API_URL,
  DOC_TYPES as ENDPOINT_TYPES,
  REPORTS_ENDPOINT,
  TRANSLATION_POLL_INTERVAL,
  TRANSLATION_POLL_TIMEOUT,
  TRANSLATION_STATUS,
} from 'qap/constants'
import { requestActions } from 'state/request'
import { tokenInvalidErrorPresent } from 'state/request/sagas'
import { uploadFile } from 'state/uploads/sagas'
import { uploadFailure } from 'state/uploads/actions'
import { fetch } from 'api'
import { fetchMemberships } from 'state/memberships/actions'
import {
  updateReportUserStatus,
  setSingleReportReviewStatus,
  setSingleReportFollowUpLabel,
  beginInlineUpdate,
  successInlineUpdate,
  failureInlineUpdate,
} from 'state/reports/actions'
import { extractFailureError } from 'state/reports/inlineUpdates'
import type {
  HandleTranslationPollingAction,
  RequireReportTranslationAction,
  UpdateReportReviewsAction,
  UpdateUserReviewStatusAction,
} from './types'

function* fetchReport(action: any): Generator<any, any, any> {
  const { reportId } = action
  const endpointType = action?.options?.docType
  const url = `${API_URL}/${getEndpointByDocType(endpointType)}/${reportId}`

  yield all([
    yield put({ type: actions.FETCH_RELATED_REPORTS }),
    yield put({
      type: requestActions.AUTHED_REQUEST,
      method: 'GET',
      url,
      successAction: success(actions.FETCH_REPORT),
      failureAction: failure(actions.FETCH_REPORT),
      endpointType
    }),
  ])
}

export function* sendTranslationRequest(action: RequireReportTranslationAction): Generator<any, any, any> {
  const { id: reportId } = yield select((state) => state.report.data)
  const { languageCode } = action.payload
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/translations`
  const body = { data: decamelizeKeys({ languageCode }) } as const
  // Use 'fetch' to reduce race conditions from extra action dispatches
  const response = yield call(fetch, url, { method: 'POST', body })
  const { ok, body: result } = camelizeKeys(response)

  if (!ok || result.errors) {
    yield put(actions.setTranslationError(reportId, languageCode))
    return
  }

  yield put(actions.pollTranslations(reportId, languageCode))
}

export function* pollUntilResolved(originalReportId: string, selectedLanguage: string, url: string): Generator<any, any, any> {
  while (true) {
    // Use 'fetch' to reduce race conditions from extra action dispatches
    const response = yield call(fetch, url, { method: 'GET' })
    const { body: result } = camelizeKeys(response)

    if (result.errors) {
      throw new Error('API error in translation polling')
    }

    const requiredTranslation = result.data.find(
// @ts-expect-error - TS7006 - Parameter 'translation' implicitly has an 'any' type.
      (translation) => translation.languageCode === selectedLanguage
    )

    if (!requiredTranslation) {
      yield delay(TRANSLATION_POLL_INTERVAL)
      continue
    }

    const isTerminalState = [
      TRANSLATION_STATUS.ready,
      TRANSLATION_STATUS.failed
    ].includes(requiredTranslation.status)

    if (!isTerminalState) {
      yield delay(TRANSLATION_POLL_INTERVAL)
      continue
    }

    yield put({
      type: success(actions.POLL_REPORT_TRANSLATIONS),
      payload: {
        translations: result.data,
        url: requiredTranslation.url,
        reportId: originalReportId,
        languageCode: selectedLanguage,
      },
    })

    if (requiredTranslation.status === TRANSLATION_STATUS.ready) {
      return true
    }

    if (requiredTranslation.status === TRANSLATION_STATUS.failed) {
      throw new Error(requiredTranslation.error)
    }
  }
}

export function* handleTranslationPolling(action: HandleTranslationPollingAction): Generator<any, any, any> {
  const { selectedLanguage } = yield select(state => state.report)
  const { id: reportId } = yield select(state => state.report.data)
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/translations`

  try {
    const result = yield race({
      ready: call(pollUntilResolved, reportId, selectedLanguage, url),
      timeout: delay(TRANSLATION_POLL_TIMEOUT),
      cancel: take([actions.CLEAR_REPORT_DATA, actions.FETCH_REPORT])
    })

    if (result.cancel) return

    if (result.timeout) {
      throw new Error('Translation polling timed out')
    }
  } catch (e: any) {
    console.error('Translation polling failed:', e)
    yield put(actions.setTranslationError(reportId, selectedLanguage))
  }
}

function* fetchRelatedReports(action: any): Generator<any, any, any> {
  const { endpointType } = action
  const {
    data: {
      reportType,
      program: { id: program }
    }
  } = yield select(state => state.report)

  const query = { filter: { program, reportType }, sort: '-generation_time' } as const
  const url = `${API_URL}/${getEndpointByDocType(endpointType)}?${stringify(query)}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url,
    successAction: success(actions.FETCH_RELATED_REPORTS),
    failureAction: failure(actions.FETCH_RELATED_REPORTS)
  })
}

function* fetchReportActivity(action: any): Generator<any, any, any> {
  const { newOffset, endpointType } = action

  if (endpointType === ENDPOINT_TYPES.certificate) {
    return
  }

  const {
    data: { id },
    activityPagination: { offset, limit }
  } = yield select(state => state.report)
  // overfetch to enable next button
  const query = { page: { offset: newOffset || offset, limit: limit+1 } } as const

  const baseUrl = `${API_URL}/${REPORTS_ENDPOINT}/${id}/activity`
  const url = `${baseUrl}?${stringify(query)}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url,
    successAction: success(actions.API_REPORTS_ACTIVITY_INDEX),
    failureAction: failure(actions.API_REPORTS_ACTIVITY_INDEX)
  })
}

function* fetchReportSuccess(action: any): Generator<any, any, any> {
  const {
    endpointType: docType,
    data: {
      id,
      participant: { id: participantId }
    }
  } = action

  yield all([
    yield put(updateReportUserStatus({
      reportId: id,
      read: true,
      docType
    })),
    yield put(fetchMemberships(participantId))
  ])
}

function * handleAttachmentErrors (uploadKey: string, status: string, errors: Array<any>) {
  if (tokenInvalidErrorPresent(status, errors)) {
    yield put(expireSession())
  } else {
// @ts-expect-error - TS2345 - Argument of type 'object' is not assignable to parameter of type 'Error'.
    yield put(uploadFailure(uploadKey, camelizeKeys(errors)))
    yield put({
      type: failure(actions.API_REPORTS_ATTACHMENTS_CREATE),
      errors: camelizeKeys(errors)
    })
  }
}

function * uploadAttachmentFile (action: any, data: any) {
  const { reportId, attachment: { file, uploadKey } } = action
  const { id: attachmentId, putUrl: url } = camelizeKeys(data)
  const key = uploadKey || url
  yield uploadFile({ key, objectId: attachmentId, url, file })
  const { success, error } = yield select(state => state.uploads[key] || {})
  if (success) {
    yield put({
      type: actions.API_REPORTS_ATTACHMENTS_UPDATE,
      reportId,
      attachment: { id: attachmentId, status: 'uploaded' }
    })
  } else if (error) {
    yield put({
      type: failure(actions.API_REPORTS_ATTACHMENTS_CREATE),
      reportId,
      errors: error
    })
  }
}

function* createReportAttachment(action: any): Generator<any, any, any> {
  const { reportId, attachment: { file, uploadKey, capa } } = action
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/attachments`
  const body = {
    data: decamelizeKeys({
      filename: file.name,
      title: file.name,
      contentType: file.type,
      capa
    })
  } as const
  const method = 'POST'

  try {
    const response = yield call(fetch, url, { body, method })
    const result = response.body

    if (response.ok === false ) {
      const errors = result.errors || []
      yield handleAttachmentErrors(uploadKey, response.status, errors)
    } else {
      yield uploadAttachmentFile(action, result.data)
    }
  } catch (e: any) {
    yield put({
      type: failure(actions.API_REPORTS_ATTACHMENTS_CREATE),
      errors: camelizeKeys(e)
    })
  }
}

function* updateReportAttachment(action: any): Generator<any, any, any> {
  const { id, ...data } = action.attachment
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/attachments/${id}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PUT',
    url,
    body: { data },
    successAction: success(actions.API_REPORTS_ATTACHMENTS_UPDATE),
    failureAction: failure(actions.API_REPORTS_ATTACHMENTS_UPDATE)
  })
}

function* deleteReportAttachment(action: any): Generator<any, any, any> {
  const { id } = action.attachment
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/attachments/${id}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'DELETE',
    url,
    successAction: success(actions.API_REPORTS_ATTACHMENTS_DESTROY),
    failureAction: failure(actions.API_REPORTS_ATTACHMENTS_DESTROY)
  })
}

function* createReportComment(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/comments`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url,
    body: { data: action.comment },
    successAction: success(actions.API_REPORTS_COMMENTS_CREATE),
    failureAction: failure(actions.API_REPORTS_COMMENTS_CREATE)
  })
}

function* updateReportComment(action: any): Generator<any, any, any> {
  const { id, ...data } = action.comment
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/comments/${id}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PUT',
    url,
    body: { data },
    successAction: success(actions.API_REPORTS_COMMENTS_UPDATE),
    failureAction: failure(actions.API_REPORTS_COMMENTS_UPDATE)
  })
}

function* deleteReportComment(action: any): Generator<any, any, any> {
  const { id } = action.comment
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/comments/${id}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'DELETE',
    url,
    successAction: success(actions.API_REPORTS_COMMENTS_DESTROY),
    failureAction: failure(actions.API_REPORTS_COMMENTS_DESTROY)
  })
}

export function* updateReportReviews(action: UpdateReportReviewsAction): Generator<any, any, any> {
  const { reportId, addedIds, removedIds } = action
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/reviewers`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url,
    body: { data: decamelizeKeys({ add: addedIds, remove: removedIds }) },
    successAction: success(actions.API_REPORTS_REVIEWERS_UPDATE),
    failureAction: failure(actions.API_REPORTS_REVIEWERS_UPDATE)
  })
}

export function* updateUserReviewStatus(action: UpdateUserReviewStatusAction): Generator<any, any, any> {
  const { reportId, status } = action
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/user_review`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url,
    body: { data: { status } },
    successAction: success(actions.API_REPORTS_USER_REVIEWS_CREATE),
    failureAction: failure(actions.API_REPORTS_USER_REVIEWS_CREATE)
  })
}

function* updateReportReviewStatus(action: any): Generator<any, any, any> {
  const { reportId, reviewStatus, inline = true } = action
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/review_status`

  if (inline) yield put(beginInlineUpdate(reportId, 'reviewStatus'))

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PUT',
    url,
    body: { data: decamelizeKeys({ reviewStatus }) },
    successAction: success(actions.API_REPORTS_REVIEW_STATUSES_UPDATE),
    failureAction: failure(actions.API_REPORTS_REVIEW_STATUSES_UPDATE),
    context: { reportId, inline },
  })
}

export function* updateReportReviewStatusInList(action: any): Generator<any, any, any> {
  const { id, reviewStatus } = action.data ?? {}
  if (!id) return

  yield put(setSingleReportReviewStatus(id, reviewStatus))
  if (action.context?.inline) yield put(successInlineUpdate(id, 'reviewStatus'))
}

export function* updateReportFollowUpLabelInList(action: any): Generator<any, any, any> {
  const { id, followUpLabel, customFollowUpLabel, labelNote = '' } = action.data ?? {}
  if (!id) return

  yield put(setSingleReportFollowUpLabel(id, followUpLabel, customFollowUpLabel, labelNote))
  if (action.context?.inline) yield put(successInlineUpdate(id, 'followUpLabel'))
}

function* failToUpdateReportReviewStatusInList(action: any): Generator<any, any, any> {
  if (!action.context?.inline) return
  const reportId = action.context?.reportId
  if (!reportId) return
  yield put(failureInlineUpdate(reportId, 'reviewStatus', extractFailureError(action)))
}

function* failToUpdateReportFollowUpLabel(action: any): Generator<any, any, any> {
  if (!action.context?.inline) return
  const reportId = action.context?.reportId
  if (!reportId) return
  yield put(failureInlineUpdate(reportId, 'followUpLabel', extractFailureError(action)))
}

function* updateReportFollowUpLabel(action: any): Generator<any, any, any> {
  const { reportId, followUpLabel, customFollowUpLabelId, labelNote, inline = true } = action
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${reportId}/follow_up_label`

  if (inline) yield put(beginInlineUpdate(reportId, 'followUpLabel'))

  const data = decamelizeKeys({
    followUpLabel,
    customFollowUpLabelId,
    labelNote
  })

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PUT',
    url,
    body: { data },
    successAction: success(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE),
    failureAction: failure(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE),
    context: { reportId, inline },
  })
}

function* fetchReportCustomFollowUpLabels(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/follow_up_labels`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    hideLoading: true,
    method: 'GET',
    url,
    successAction: success(actions.API_REPORTS_FOLLOW_UP_LABELS_INDEX),
    failureAction: failure(actions.API_REPORTS_FOLLOW_UP_LABELS_INDEX)
  })
}

// Report CAPA sagas
function* createReportCAPA(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/capa`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url,
    body: { data: action.capaData },
    successAction: success(actions.API_REPORTS_CAPAS_CREATE),
    failureAction: failure(actions.API_REPORTS_CAPAS_CREATE)
  })
}

function* updateReportCAPA(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/capa`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PATCH',
    url,
    body: { data: action.capaData },
    successAction: success(actions.API_REPORTS_CAPAS_UPDATE),
    failureAction: failure(actions.API_REPORTS_CAPAS_UPDATE)
  })
}

function* closeReportCAPA(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/capa/close`
  const requestBody = action.capaData ? { data: action.capaData } : undefined

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url,
    body: requestBody,
    successAction: success(actions.API_REPORTS_CAPAS_CLOSE),
    failureAction: failure(actions.API_REPORTS_CAPAS_CLOSE)
  })
}

function* reopenReportCAPA(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}/capa/reopen`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url,
    body: action.capaData,
    successAction: success(actions.API_REPORTS_CAPAS_REOPEN),
    failureAction: failure(actions.API_REPORTS_CAPAS_REOPEN)
  })
}

export default function* reportSagas(): Generator<any, any, any> {
  yield all([
    yield takeLatest(actions.FETCH_REPORT, fetchReport),
    yield takeLatest(actions.REQUIRE_REPORT_TRANSLATION, sendTranslationRequest),
    yield takeLatest(actions.POLL_REPORT_TRANSLATIONS, handleTranslationPolling),
    yield takeEvery(success(actions.FETCH_REPORT), fetchReportSuccess),
    yield takeEvery(success(actions.FETCH_REPORT), fetchRelatedReports),
    yield takeEvery(success(actions.FETCH_REPORT), fetchReportActivity),
    yield takeEvery(actions.API_REPORTS_ATTACHMENTS_CREATE, createReportAttachment),
    yield takeEvery(actions.API_REPORTS_ATTACHMENTS_UPDATE, updateReportAttachment),
    yield takeEvery(actions.API_REPORTS_ATTACHMENTS_DESTROY, deleteReportAttachment),
    yield takeEvery(actions.API_REPORTS_COMMENTS_CREATE, createReportComment),
    yield takeEvery(actions.API_REPORTS_COMMENTS_UPDATE, updateReportComment),
    yield takeEvery(actions.API_REPORTS_COMMENTS_DESTROY, deleteReportComment),
// @ts-expect-error - TS2769 - No overload matches this call.
    yield takeEvery(actions.API_REPORTS_REVIEWERS_UPDATE, updateReportReviews),
// @ts-expect-error - TS2769 - No overload matches this call.
    yield takeEvery(actions.API_REPORTS_USER_REVIEWS_CREATE, updateUserReviewStatus),
    yield takeEvery(actions.API_REPORTS_REVIEW_STATUSES_UPDATE, updateReportReviewStatus),
    yield takeEvery(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE, updateReportFollowUpLabel),
    yield takeEvery(success(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE), updateReportFollowUpLabelInList),
    yield takeEvery(success(actions.API_REPORTS_REVIEW_STATUSES_UPDATE), updateReportReviewStatusInList),
    yield takeEvery(failure(actions.API_REPORTS_REVIEW_STATUSES_UPDATE), failToUpdateReportReviewStatusInList),
    yield takeEvery(failure(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE), failToUpdateReportFollowUpLabel),
    yield takeLatest(actions.API_REPORTS_FOLLOW_UP_LABELS_INDEX, fetchReportCustomFollowUpLabels),
    yield takeLatest(actions.API_REPORTS_ACTIVITY_INDEX, fetchReportActivity),
    yield takeLatest(actions.API_REPORTS_CAPAS_CREATE, createReportCAPA),
    yield takeLatest(actions.API_REPORTS_CAPAS_UPDATE, updateReportCAPA),
    yield takeLatest(actions.API_REPORTS_CAPAS_CLOSE, closeReportCAPA),
    yield takeLatest(actions.API_REPORTS_CAPAS_REOPEN, reopenReportCAPA)
  ])
}
